import { CanvasCommand, CreateElementCommand, DeleteElementCommand, GroupCommand } from "../../command";
import { TextChangeResult } from "../../stepHandles/steps/text";
import { RegionElement, StepService, CommandWithFollowup, ModuleStepData, Step, LayoutsState, Region, ILayout, IllustrationElement, LayoutElementType } from "../../types";
import { generate } from "../../util/guid";
import { WorkflowManager } from "../../WorkflowManager";
import { split } from "unicode-default-word-boundary";

class ModuleStepService implements StepService<ModuleStepData> {
    async init(
        stepData: Step<ModuleStepData>,
        workflowManager: WorkflowManager,
        reducerState?: LayoutsState,
    ): Promise<CommandWithFollowup | null> {
        if (reducerState) {
            await this.reload(stepData, workflowManager, reducerState);
        } else {
            workflowManager.updateMetadata(stepData.stepName, { text: "" });
            return this.changeTextCommand(stepData, "", workflowManager, () => {});
        }
        return null;
    }

    private async reload(stepData: Step<ModuleStepData>, workflowManager: WorkflowManager, reducerState: LayoutsState) {
        const serializedStep = workflowManager.getSerializedStep(
            stepData.stepName,
            reducerState.serializableWorkflow.steps,
        );
        const layouts = Object.values(reducerState.layouts);
        const elements = layouts.map((layout) => layout.elements).flat();
        const elementsForStep = elements.filter((el) => el.stepName === stepData.stepName);
        const regionElements = elementsForStep.map((el) => ({ id: el.id, region: el.stepRegion }));
        await workflowManager.setSelectionsAndElements(stepData.stepName, [], regionElements, async () => {
            workflowManager.updateStorage(stepData.stepName, { text: serializedStep?.storage?.text });
            workflowManager.setMandatoryFulfilled(stepData.stepName, serializedStep?.storage?.text !== "");
        });
    }

    async changeText(
        stepData: Step<ModuleStepData>,
        input: string,
        workflowManager: WorkflowManager,
        setError: (e: string) => void,
    ) {
        const result = await this.changeTextCommand(stepData, input, workflowManager, setError);
        if (result) {
            result.command && workflowManager.getCommandDispatcher()(result.command);
            result.followup && (await result.followup());
        }
    }

    private async changeTextCommand(
        stepData: Step<ModuleStepData>,
        input: string,
        workflowManager: WorkflowManager,
        setError: (e: string) => void,
    ): Promise<CommandWithFollowup | null> {
        const elements = workflowManager.getRegionElements(stepData.stepName);

        const module = workflowManager.getStepSpecificServices(stepData.stepName)?.module;
        if (!module) {
            console.error("Missing module.");
            return null;
        }

        const profanityFilter = workflowManager.getProfanities();
        const validationResult = this.validateInput(stepData, input, profanityFilter, setError);
        if (validationResult.errorData) {
            workflowManager.setMandatoryFulfilled(stepData.stepName, false);
            if (validationResult.errorData.blockedProfanity) {
                console.error("Blocked profanity.");
            } else if (validationResult.errorData.hitCharacterLimit) {
                console.error("Too many characters.");
            } else if (validationResult.errorData.unsupportedCharacters) {
                console.error("Unsupported characters.");
            }
            return null;
        }
        workflowManager.setMandatoryFulfilled(stepData.stepName, input !== "");

        if (!stepData.data || !stepData.data.regions || stepData.data.regions.length <= 0) {
            console.error("Missing configuration.");
            return null;
        }

        workflowManager.updateStorage(stepData.stepName, { text: input });
        workflowManager.updateMetadata(stepData.stepName, { text: input });

        const createElementFromRegion = (svg: string, region: Region, existingKey?: string) => {
            const elementKey = existingKey || generate();
            const layouts = workflowManager.getLayouts();
            const layout = layouts.find((layout: ILayout) => layout.panelId === region.panelId);
            if (!layout) {
                console.error(`Can not find layout for region: ${region.panelId}`);
                return null;
            }

            const commands: CanvasCommand[] = [];
            if (existingKey) {
                commands.push(new DeleteElementCommand(elementKey));
            }
            commands.push(
                new CreateElementCommand<IllustrationElement>(
                    {
                        stepRegion: region,
                        stepName: stepData.stepName,
                        colors: {},
                        id: elementKey,
                        svg: svg,
                        type: LayoutElementType.Illustration,
                        y: region.top,
                        x: region.left,
                        rotation: region.rotation,
                        width: region.width,
                        height: region.height,
                        layer: region.layer,
                        layerIndex: region.layerIndex,
                        immutable: region.immutable,
                    },
                    layout,
                ),
            );
            return {
                id: elementKey,
                region,
                command: new GroupCommand(commands),
            };
        };

        if (elements.length > 0) {
            // Create new elements from passed in elements' regions.
            const results = elements
                .map((regionElement) => {
                    if (!regionElement.region) {
                        return null;
                    }
                    const svg = module.svgPrint(input, regionElement.region);
                    return createElementFromRegion(svg, regionElement.region, regionElement.id);
                })
                .filter((x) => !!x);
            const commands = results.filter((r) => !!r).map((r) => r && r.command);
            return {
                command: new GroupCommand(commands as CanvasCommand[]),
                followup: async () => {},
            };
        } else {
            // Create new elements from workflow step's regions.
            const results = stepData.data.regions.map((region) =>
                createElementFromRegion(module.svgPrint(input, region), region),
            );
            const commands = results.filter((r) => !!r).map((r) => r && r.command);
            return {
                command: new GroupCommand(commands as CanvasCommand[]),
                followup: async () => {
                    const newElements = results.filter((r) => r).map((r) => r && { id: r.id, region: r.region });
                    await workflowManager.setSelectionsAndElements(
                        stepData.stepName,
                        [],
                        newElements as RegionElement[],
                    );
                },
            };
        }
    }

    private validateInput(
        stepData: Step<ModuleStepData>,
        input: string,
        profanityFilter: string[],
        setError: (e: string) => void,
    ): TextChangeResult {
        if (stepData.data && stepData.data.maxLength) {
            const maxLength = stepData.data.maxLength;
            if (input.length > maxLength) {
                setError("Too many characters.");
                return { input, helperData: {},  errorData: { hitCharacterLimit: true } };
            }
        }

        // Regex for filtering unwanted characters
        if (!/^$|^[a-zA-Z0-9 ]+$/.test(input)) {
            setError("Unsupported characters.");
            return { input, helperData: {},  errorData: { unsupportedCharacters: true } };
        }

        // Check text against our blacklist
        const words = split(input.toLowerCase());
        for (const word of words) {
            for (const i in profanityFilter) {
                const profanity = profanityFilter[i].toLowerCase().replace(/\s/g, "");
                const match = word === profanity;
                if (match) {
                    setError("Blocked profanity.");
                    return { input, helperData: {},  errorData: { blockedProfanity: true } };
                }
            }
        }

        setError("");
        return { input, helperData: {} };
    }
}

export const moduleStepService = new ModuleStepService();
